import { useMemo } from "react";
import { Picker } from "@design-system/Picker/Picker.lib";

export interface SortSelectOption<TKey extends string> {
    key: TKey;
    label: string;
}

interface SortSelectProps<TKey extends string> {
    value: TKey;
    /** The option representing the stock order; selecting it reads "Sort by". */
    defaultKey: TKey;
    options: Array<SortSelectOption<TKey>>;
    onValueChange: (value: TKey | null) => void;
}

const lowercaseFirst = (value: string) =>
    value.charAt(0).toLowerCase() + value.slice(1);

export const SortSelect = <TKey extends string>({
    value,
    defaultKey,
    options,
    onValueChange,
}: SortSelectProps<TKey>) => {
    const selected = options.find((option) => option.key === value);
    const triggerValue =
        !selected || selected.key === defaultKey
            ? "Sort by"
            : `Sort: ${lowercaseFirst(selected.label)}`;
    const items = useMemo(
        () =>
            options.map((option) => ({
                value: option.key,
                label: option.label,
            })),
        [options],
    );

    return (
        <Picker.Select
            items={items}
            value={value}
            onValueChange={onValueChange}
            renderValue={() => triggerValue}
        />
    );
};
